import type { AttributeStateResponse } from '../../api/types';
import { CONFIDENCE_COPY } from './attributeDisplay';
import styles from './AttributeRadar.module.css';

/**
 * The Snapshot's attribute-profile visualisation (Mission 171, "Snapshot +
 * Detailed Profile Experience", §6G) — a Football-Manager-inspired polygon
 * of this person's own assessed attribute ratings, and nothing else.
 *
 * ---------------------------------------------------------------------
 * EVERY VERTEX IS AN EXISTING RATING — NOTHING IS AVERAGED OR INVENTED
 *
 * One axis per *assessed* attribute (`composite !== null`), plotted at
 * exactly that attribute's own existing 1-20 composite. There is no
 * per-category aggregate here: the attribute framework's seven categories
 * (`docs/ATTRIBUTE_FRAMEWORK.md` §3, `attribute_category` in
 * `database/migrations/0022_seed_reference_data.js`) are used only to
 * *order* the axes so related attributes sit together, never to compute a
 * category composite — that would be exactly the kind of invented derived
 * figure Mission 171's own brief rules out (§2). Unassessed attributes get
 * no axis at all, the same "absence, not a zero" discipline
 * `bandFor`/`AttributesSection` already apply — a missing axis says
 * nothing, where a zero-length one would read as a rock-bottom rating.
 *
 * DEGRADES TO A LIST, NEVER A MEANINGLESS SHAPE
 *
 * A polygon needs at least three vertices to read as a shape at all; below
 * that (`attributes.length < 3`), this renders the same per-attribute rows
 * `AttributeDataList` always produces, promoted to the primary, visible
 * content instead of staying the screen-reader-only fallback.
 *
 * ALWAYS A TEXT ALTERNATIVE — NOT ONLY FOR SCREEN READERS
 *
 * `AttributeDataList` (name, rating, confidence, per attribute) is the
 * single source both consumers read from: it stays screen-reader-only on
 * wide viewports where the chart is the visible presentation (§14's "the
 * Snapshot should remain understandable without visual interpretation of
 * the radar"), and `AttributeRadar.module.css`'s own narrow-viewport query
 * swaps which of the two is visually hidden, rather than hiding the chart
 * and showing nothing in its place.
 * ---------------------------------------------------------------------
 */
export function AttributeRadar({ attributes }: { attributes: AttributeStateResponse[] }) {
  const assessed = attributes
    .filter((state) => state.composite !== null)
    .slice()
    .sort((a, b) => {
      const categoryOrder = a.attribute.categoryDisplayOrder - b.attribute.categoryDisplayOrder;
      if (categoryOrder !== 0) return categoryOrder;
      return a.attribute.name.localeCompare(b.attribute.name);
    });

  if (assessed.length === 0) {
    return <p className={styles.absent}>No attribute has a genuine assessment yet, so there is nothing to plot.</p>;
  }

  return (
    <div className={styles.wrap}>
      {assessed.length >= 3 ? <RadarChart assessed={assessed} /> : null}
      <AttributeDataList assessed={assessed} forceVisible={assessed.length < 3} />
    </div>
  );
}

const CHART_SIZE = 320;
const CENTER = CHART_SIZE / 2;
const MAX_RADIUS = CENTER - 56; // leaves room for labels around the edge
const RING_VALUES = [4, 8, 12, 16, 20]; // the five band boundaries, docs/ASSESSMENT_FRAMEWORK.md §2.2

function pointFor(angle: number, radius: number): { x: number; y: number } {
  return {
    x: CENTER + radius * Math.sin(angle),
    y: CENTER - radius * Math.cos(angle),
  };
}

function polygonPoints(values: number[], maxValue: number): string {
  const step = (2 * Math.PI) / values.length;
  return values
    .map((value, index) => {
      const { x, y } = pointFor(index * step, MAX_RADIUS * (value / maxValue));
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

function RadarChart({ assessed }: { assessed: AttributeStateResponse[] }) {
  const step = (2 * Math.PI) / assessed.length;
  const ratings = assessed.map((state) => state.composite!.composite);

  return (
    <svg
      className={styles.chart}
      viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}
      role="img"
      aria-label={`Attribute profile: ${assessed.length} assessed attributes, plotted on a 1 to 20 scale.`}
    >
      {/* Band-boundary rings — a reading aid, never a judgement: same
          reasoning tokens.css's own "ON COLOUR AND JUDGEMENT" note gives
          for never drawing the 1-20 bands red-to-green. */}
      {RING_VALUES.map((ringValue) => (
        <polygon key={ringValue} className={styles.ring} points={polygonPoints(assessed.map(() => ringValue), 20)} />
      ))}

      {/* One spoke per axis. */}
      {assessed.map((state, index) => {
        const { x, y } = pointFor(index * step, MAX_RADIUS);
        return <line key={state.attribute.id} className={styles.spoke} x1={CENTER} y1={CENTER} x2={x} y2={y} />;
      })}

      {/* The person's own ratings — the only thing on this chart that is
          actually theirs. */}
      <polygon className={styles.dataPolygon} points={polygonPoints(ratings, 20)} />
      {assessed.map((state, index) => {
        const { x, y } = pointFor(index * step, MAX_RADIUS * (state.composite!.composite / 20));
        return <circle key={state.attribute.id} className={styles.dataPoint} cx={x} cy={y} r={3} />;
      })}

      {/* Labels, anchored by quadrant so text reads outward rather than
          through the chart. */}
      {assessed.map((state, index) => {
        const angle = index * step;
        const { x, y } = pointFor(angle, MAX_RADIUS + 14);
        const cos = Math.sin(angle);
        const sin = -Math.cos(angle);
        const anchor = cos > 0.15 ? 'start' : cos < -0.15 ? 'end' : 'middle';
        const baseline = sin > 0.5 ? 'hanging' : sin < -0.5 ? 'auto' : 'middle';
        return (
          <text key={state.attribute.id} className={styles.label} x={x} y={y} textAnchor={anchor} dominantBaseline={baseline}>
            {state.attribute.name}
          </text>
        );
      })}
    </svg>
  );
}

/** The accessible (and, below three axes or on a narrow viewport, the only
 * visible) representation — every value the chart plots, in words. */
function AttributeDataList({ assessed, forceVisible }: { assessed: AttributeStateResponse[]; forceVisible: boolean }) {
  return (
    <ul className={forceVisible ? styles.dataListVisible : styles.dataList}>
      {assessed.map((state) => (
        <li key={state.attribute.id} className={styles.dataRow}>
          <span className={styles.dataName}>{state.attribute.name}</span>
          <span className={styles.dataCategory}>{state.attribute.categoryLabel}</span>
          <span className={styles.dataValue}>{state.composite!.composite.toFixed(1)}</span>
          <span className={styles.dataConfidence}>{CONFIDENCE_COPY[state.confidence.level].label}</span>
        </li>
      ))}
    </ul>
  );
}
